import React from 'react';
import { Link } from 'react-router';

import { listingSourcesApi } from '../../requests/listingSources';
import { CountChips, ModePill, OutcomePill, StatusPill } from './Pills';
import {
    OUTCOMES,
    OUTCOME_LABEL,
    changeKind,
    formatClock,
    formatNumber,
    formatRange,
    formatRunTime,
    formatValue,
    zoneAbbreviation,
} from './format';

const CHANGES_SHOWN = 4;

/**
 * What one run did, listing by listing.
 *
 * This is the answer to "why does this property say that?", so a row has to
 * carry the decision, the reason behind it, and the columns that moved —
 * including the ones that were emptied, which is most of them when a property
 * changes shape.
 */
export default class RunDetail extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            run: props.run,
            outcome: null,
            items: [],
            meta: null,
            page: 1,
            loading: true,
            error: null,
            expanded: {},
        };
    }

    componentDidMount() {
        // The run arrives from whichever list was clicked, which may hold only
        // an id — the search results know the run number and nothing else.
        this.setState({ outcome: this.defaultOutcome(this.props.run) }, () => this.load(1));
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    /** Open on whatever this run has something to say about. */
    defaultOutcome(run) {
        if (!run || run.created === undefined) return 'updated';
        if (run.failed) return 'failed';
        if (run.updated) return 'updated';
        if (run.created) return 'created';
        return 'skipped';
    }

    load(page) {
        this.setState({ loading: true, error: null });

        listingSourcesApi.runItems(this.props.run.id, this.state.outcome, page).then(
            (envelope) => {
                if (this.unmounted) return;
                this.setState({
                    items: envelope.data,
                    meta: envelope.meta && envelope.meta.pagination,
                    page,
                    loading: false,
                });
            },
            (error) => {
                if (this.unmounted) return;
                this.setState({ loading: false, error: error.message });
            }
        );
    }

    pick(outcome) {
        this.setState({ outcome, expanded: {} }, () => this.load(1));
    }

    toggleExpanded(id) {
        const expanded = Object.assign({}, this.state.expanded);
        expanded[id] = !expanded[id];
        this.setState({ expanded });
    }

    renderChanges(item) {
        const entries = Object.keys(item.changes || {}).map((column) => [column, item.changes[column]]);
        const open = this.state.expanded[item.id];
        const shown = open ? entries : entries.slice(0, CHANGES_SHOWN);

        // Created rows carry the whole property. Thirty-odd rows of "— → value"
        // is not a diff anybody reads, so the count stands in for it.
        if (item.outcome === 'created' && entries.length > CHANGES_SHOWN && !open) {
            return (
                <div>
                    <div className="ls-change__note">{entries.length} columns written</div>
                    <button type="button" className="ls-linkbtn" onClick={() => this.toggleExpanded(item.id)}>
                        Show them
                    </button>
                </div>
            );
        }

        const blanks = (item.notes || []).filter((note) => note.column);

        return (
            <div>
                {shown.map(([column, change]) => {
                    const kind = changeKind(change.from, change.to);
                    return (
                        <div key={column} className={'ls-change is-' + kind}>
                            <span className="ls-change__marker">
                                {kind === 'cleared' ? '−' : kind === 'added' ? '+' : '→'}
                            </span>
                            <span className="ls-mono ls-change__column" title={column}>{column}</span>
                            <span className="ls-change__from" title={String(change.from)}>
                                {formatValue(change.from, column)}
                            </span>
                            <span className="ls-change__arrow">→</span>
                            <span className="ls-change__to" title={String(change.to)}>
                                {kind === 'cleared' ? 'cleared' : formatValue(change.to, column)}
                            </span>
                        </div>
                    );
                })}

                {blanks.map((note) => (
                    <div key={note.column} className="ls-blank">
                        <span className="ls-blank__mark">?</span>
                        <span className="ls-mono ls-blank__column">{note.column} left blank</span>
                        {note.value && <span className="ls-blank__word">source said “{note.value}”</span>}
                    </div>
                ))}

                {entries.length > CHANGES_SHOWN && (
                    <button type="button" className="ls-linkbtn" onClick={() => this.toggleExpanded(item.id)}>
                        {open ? 'Show fewer' : '+' + (entries.length - CHANGES_SHOWN) + ' more columns'}
                    </button>
                )}

                {entries.length === 0 && blanks.length === 0 && <span className="ls-change__none">—</span>}
            </div>
        );
    }

    renderItems() {
        const { items, loading, error, meta, page, outcome } = this.state;

        if (error) return <div className="ls-empty">Could not load these listings. {error}</div>;
        if (loading && items.length === 0) return <div className="ls-empty">Loading listings…</div>;

        if (items.length === 0) {
            return (
                <div className="ls-empty">
                    No {(OUTCOME_LABEL[outcome] || outcome).toLowerCase()} listings in this run
                </div>
            );
        }

        const lastPage = meta ? meta.last_page : 1;

        return (
            <div>
                <div className="ls-table__scroll">
                    <div className="ls-table ls-table--items">
                        <div className="ls-table__head">
                            <div>Listing</div>
                            <div>Outcome</div>
                            <div>Why</div>
                            <div>What changed</div>
                            <div>Property</div>
                        </div>

                        {items.map((item) => (
                            <div key={item.id} className="ls-table__row is-static">
                                <div className="ls-mono ls-item__id">{item.external_id}</div>
                                <div><OutcomePill outcome={item.outcome} /></div>
                                <div className="ls-item__reason">{item.reason}</div>
                                <div>{this.renderChanges(item)}</div>
                                <div>
                                    {item.property_id ? (
                                        <Link className="ls-item__property" to={'/property/' + item.property_id}>
                                            #{item.property_id}
                                        </Link>
                                    ) : (
                                        <span className="ls-item__noproperty">—</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="ls-pager">
                    <span className="ls-pager__label">{formatRange(meta, items.length)}</span>
                    <button type="button" className="ls-btn ls-btn--small" disabled={page <= 1}
                        onClick={() => this.load(page - 1)}>
                        Previous
                    </button>
                    <button type="button" className="ls-btn ls-btn--small" disabled={page >= lastPage}
                        onClick={() => this.load(page + 1)}>
                        Next
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const run = this.props.run;
        const zone = zoneAbbreviation(this.props.source.settings.timezone);
        const known = run.created !== undefined;

        return (
            <div className="ls-detail">
                <button type="button" className="ls-backlink" onClick={this.props.onBack}>
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor"
                        strokeWidth="1.7" aria-hidden="true">
                        <path d="M8.4 2.8 L4.4 6.5 L8.4 10.2" />
                    </svg>
                    All runs
                </button>

                <div className="ls-detail__head">
                    <h1 className="ls-detail__title">{formatRunTime(run.started_at)}</h1>
                    {run.status && <StatusPill status={run.status} />}
                    {run.mode && <ModePill mode={run.mode} />}
                    <span className="ls-detail__time">
                        run #{run.id}
                        {run.finished_at ? ' · finished ' + formatClock(run.finished_at) + ' ' + zone : ''}
                        {run.started_by ? ' · ' + run.started_by : ''}
                    </span>
                </div>

                {known && <CountChips run={run} />}

                {run.error && <div className="ls-detail__error">{run.error}</div>}

                <div className="ls-filters">
                    {OUTCOMES.map((key) => (
                        <button
                            key={key}
                            type="button"
                            className={'ls-filter' + (this.state.outcome === key ? ' is-active' : '')}
                            onClick={() => this.pick(key)}
                        >
                            {OUTCOME_LABEL[key]}
                            {known && (
                                <span className={'ls-filter__count' + (run[key] ? '' : ' is-zero')}>
                                    {formatNumber(run[key])}
                                </span>
                            )}
                        </button>
                    ))}

                    {/* Counted, never listed — the source had nothing new to say,
                        so there is no row to show. Present but plainly inert,
                        because its absence reads as a missing filter. */}
                    <span
                        className="ls-filter is-inert"
                        title="The source had nothing new to say, so no rows exist"
                    >
                        Unchanged
                        {known && <span className="ls-filter__count">{formatNumber(run.unchanged)}</span>}
                        <span className="ls-filter__aside">no rows</span>
                    </span>
                </div>

                <div className="ls-card">{this.renderItems()}</div>
            </div>
        );
    }
}
